import React, { Component } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import BlockUi from 'react-block-ui';
import { Button, Card, CardBody, CardHeader, Col, CustomInput, Form, Input, InputGroup, InputGroupAddon, Label, Row } from 'reactstrap';
import { deleteAPI, getAPI, putAPI } from '../Base/API';
import { ErrorAlert } from '../Common/ErrorAlert';
import NotificationManager from '../Base/NotificationCenter/NotificationStore';
import { ConfirmActionModal } from '../Base/Common/CommonUIComponents';

class ChannelConnectivityConfigSection extends Component {

    state = {
        block: true,
        error: null,
        emails: [],
        active: true,
        newEmailInput: '',
        hasExisting: false,
        confirmDelete: false
    }

    componentDidMount() {
        this.loadConfig();
    }

    loadConfig = () => {
        const { channelInstanceId } = this.props;
        if (!channelInstanceId) {
            this.setState({ block: false });
            return;
        }
        this.setState({ block: true });
        getAPI(result => {
            const { data, error } = result;
            if (error) {
                this.setState({ error: [{ message: error.message, stack: error.stack, messageType: 'danger' }], block: false });
                return;
            }
            if (data && data.errors && data.errors.length > 0) {
                NotificationManager.error(data.errors[0].message, '', 3000);
                this.setState({ block: false });
                return;
            }
            const cfg = data && data.responseList && data.responseList[0];
            if (cfg) {
                this.setState({ emails: cfg.emails || [], active: cfg.active, hasExisting: true, block: false });
            } else {
                this.setState({ emails: [], active: true, hasExisting: false, block: false });
            }
        }, `/api/Rate/ConnectivityIncident/v1/config/channel/${encodeURIComponent(channelInstanceId)}`);
    }

    addEmail = () => {
        const email = (this.state.newEmailInput || '').trim().toLowerCase();
        if (!email) return;
        if (this.state.emails.some(e => e.toLowerCase() === email)) {
            NotificationManager.warning(this.props.intl.formatMessage({ id: "connectivityConfig.emails.duplicate" }), '', 3000);
            return;
        }
        this.setState(prev => ({ emails: [...prev.emails, email], newEmailInput: '' }));
    }

    removeEmail = (email) => {
        this.setState(prev => ({ emails: prev.emails.filter(e => e !== email) }));
    }

    toggleConfirmDelete = () => {
        this.setState(prev => ({ confirmDelete: !prev.confirmDelete }));
    }

    deleteConfig = () => {
        const { channelInstanceId } = this.props;
        this.setState({ block: true, confirmDelete: false });
        deleteAPI(result => {
            const { data, error } = result;
            if (error) {
                this.setState({ error: [{ message: error.message, stack: error.stack, messageType: 'danger' }], block: false });
                return;
            }
            if (data && data.errors && data.errors.length > 0) {
                NotificationManager.error(data.errors[0].message, '', 3000);
                this.setState({ block: false });
                return;
            }
            NotificationManager.success(this.props.intl.formatMessage({ id: "connectivityConfig.deleteSuccess" }), '', 3000);
            this.setState({ block: false, emails: [], active: true, newEmailInput: '', hasExisting: false });
        }, `/api/Rate/ConnectivityIncident/v1/config/channel/${encodeURIComponent(channelInstanceId)}`);
    }

    save = (evt) => {
        if (evt) evt.preventDefault();
        const { channelInstanceId } = this.props;
        const { emails, active } = this.state;
        if (active && emails.length === 0) {
            NotificationManager.warning(this.props.intl.formatMessage({ id: "connectivityConfig.saveNeedEmails" }), '', 3000);
            return;
        }
        this.setState({ block: true });
        putAPI(result => {
            const { data, error } = result;
            if (error) {
                this.setState({ error: [{ message: error.message, stack: error.stack, messageType: 'danger' }], block: false });
                return;
            }
            if (data && data.errors && data.errors.length > 0) {
                NotificationManager.error(data.errors[0].message, '', 3000);
                this.setState({ block: false });
                return;
            }
            const cfg = data && data.responseList && data.responseList[0];
            NotificationManager.success(this.props.intl.formatMessage({ id: "connectivityConfig.saveSuccess" }), '', 3000);
            this.setState({ block: false, hasExisting: !!cfg, emails: (cfg && cfg.emails) || emails, active: cfg ? cfg.active : active });
        }, `/api/Rate/ConnectivityIncident/v1/config/channel/${encodeURIComponent(channelInstanceId)}`, JSON.stringify({ emails, active }));
    }

    render() {
        const { emails, active, newEmailInput, block, error, hasExisting, confirmDelete } = this.state;

        return (
            <BlockUi tag="div" blocking={block}>
                <Form onSubmit={this.save}>
                    <Card className="shadow border-0">
                        <ErrorAlert error={error} />
                        <CardHeader>
                            <Row className="align-items-center">
                                <Col>
                                    <i className="fas fa-envelope mr-2" />
                                    <FormattedMessage id="connectivityConfig.title" />
                                </Col>
                                <Col className="text-right">
                                    {hasExisting && (
                                        <Button
                                            type="button"
                                            color="link"
                                            className="btn-sm text-danger mr-4 p-0"
                                            onClick={this.toggleConfirmDelete}
                                            title={this.props.intl.formatMessage({ id: "connectivityConfig.delete" })}
                                        >
                                            <i className="fas fa-trash-alt" />
                                        </Button>
                                    )}
                                    <Button className="btn-host btn-sm" type="submit">
                                        <i className="fas fa-save" />
                                    </Button>
                                </Col>
                            </Row>
                        </CardHeader>
                        <CardBody>
                            <Row className="align-items-center">
                                <Col className="d-flex align-items-center">
                                    <Label className="mb-0 mr-2">
                                        <FormattedMessage id="connectivityConfig.emails.label" />
                                    </Label>
                                    <InputGroup size="sm" className="flex-grow-1">
                                        <Input
                                            type="email"
                                            maxLength={200}
                                            value={newEmailInput}
                                            onChange={(e) => this.setState({ newEmailInput: e.target.value })}
                                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); this.addEmail(); } }}
                                            placeholder={this.props.intl.formatMessage({ id: "connectivityConfig.emails.label" })}
                                        />
                                        <InputGroupAddon addonType="append">
                                            <Button
                                                type="button"
                                                className="btn btn-host btn-sm"
                                                onClick={this.addEmail}
                                                title={this.props.intl.formatMessage({ id: "connectivityConfig.emails.add" })}
                                            >
                                                <i className="fas fa-plus"></i>
                                            </Button>
                                        </InputGroupAddon>
                                    </InputGroup>
                                </Col>
                                <Col xs="auto" className="d-flex align-items-center">
                                    <Label for="connectivityActive" className="mb-0 mr-2">
                                        <FormattedMessage id="connectivityConfig.active" />
                                    </Label>
                                    <CustomInput
                                        type="switch"
                                        id="connectivityActive"
                                        name="active"
                                        checked={active}
                                        onChange={(e) => this.setState({ active: e.target.checked })}
                                    />
                                </Col>
                            </Row>
                            <Row className="mt-2">
                                <Col>
                                    {emails.length === 0 ? (
                                        <div className="text-muted small">
                                            <FormattedMessage id="connectivityConfig.emails.empty" />
                                        </div>
                                    ) : (
                                        emails.map((email) => (
                                            <span key={email} className="d-inline-flex align-items-center bg-light border rounded mr-2 mb-2 pl-3 pr-2 py-1">
                                                <i className="fas fa-envelope text-muted mr-2" style={{ fontSize: '0.85em' }}></i>
                                                <span className="mr-2">{email}</span>
                                                <Button
                                                    color="link"
                                                    className="p-0 text-danger"
                                                    style={{ lineHeight: 1 }}
                                                    onClick={() => this.removeEmail(email)}
                                                    title={this.props.intl.formatMessage({ id: "connectivityConfig.emails.remove" })}
                                                >
                                                    <i className="fas fa-times"></i>
                                                </Button>
                                            </span>
                                        ))
                                    )}
                                </Col>
                            </Row>
                        </CardBody>
                    </Card>
                </Form>
                {confirmDelete && (
                    <ConfirmActionModal
                        modal={confirmDelete}
                        toggleModal={this.toggleConfirmDelete}
                        actionFunction={this.deleteConfig}
                        text={<FormattedMessage id="connectivityConfig.deleteConfirm" />}
                    />
                )}
            </BlockUi>
        );
    }
}

export default injectIntl(ChannelConnectivityConfigSection);
